import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { fulFillmentActions } from '@detox/actions';
import { REQUEST_STATUS } from '../../constants';
import { BaseState } from '../../types/state.types';
import { KeyValue } from '../../types/common.types';
import { getStockAvailability } from '../../helpers/productDetails';
import { StockDetails } from '../../types/product.types';
import { ACTION_TYPES } from '../../constants/actions';
import { useInit } from '../useInit';
import { getUIAMEnabledWithToken } from '../../selectors';

const { getMTPOSStock } = fulFillmentActions;

export enum STOCK_CHECK_TYPE {
  ALL,
  MTPOS,
  STORE,
  STORE_MULTIPLE_SKUS
}

interface StockState {
  status: boolean;
  error: boolean;
  notAvailableSkus: string[];
  availableStoreIds: string[];
  isFetched: boolean;
  stockDetails: StockDetails;
}

const useStockCheck = ({
  type,
  storeIdPickup = [],
  eshopMtposStockEnabled,
  abTestingPending,
  isBackOrder = false,
  groupId,
  accessorySkus = [],
  deviceSkus = [],
  freebieSkus = [],
  checkStockFromStore
}: {
  type: STOCK_CHECK_TYPE;
  abTestingPending: boolean;
  eshopMtposStockEnabled: boolean;
  isBackOrder?: boolean;
  storeIdPickup?: string[];
  groupId?: string;
  accessorySkus?: string[];
  freebieSkus?: string[];
  deviceSkus?: string[];
  checkStockFromStore?: boolean;
}) => {
  const dispatch = useDispatch();

  const {
    mtposStock,
    deviceSku,
    freebies,
    accessories,
    apigeeTokenEnabled
  } = useSelector<BaseState, KeyValue>(state => {
    return {
      mtposStock: state.fulfillment?.mtposStock,
      deviceSku: state.cart?.order?.mobile?.device?.sku,
      freebies: state.cart?.cartOrder?.freebies,
      accessories: state.cart?.cartOrder?.accessories,
      apigeeTokenEnabled: getUIAMEnabledWithToken(state)
    };
  });

  const [stockAvailable, setStockAvailable] = useState<StockState>({
    status: true,
    notAvailableSkus: [],
    availableStoreIds: [],
    isFetched: false,
    error: false,
    stockDetails: {
      accessories: { stock: [], status: false },
      freebies: { stock: [], status: false },
      devices: { stock: [], status: false },
      unavailableItems: []
    }
  });

  const [loading, setLoading] = useState(false);
  const freebiesSkuIds = freebies
    ? freebies.map(freebie => freebie.skuId)
    : freebieSkus;
  const checkStocks = async () => {
    let isAvailable = true;
    let notAvailableSkus = [];
    let availableStoreIds = [];
    let hasError = false;
    let stockDetails = stockAvailable.stockDetails;

    switch (type) {
      case STOCK_CHECK_TYPE.ALL: {
        if (isBackOrder) {
          isAvailable = true;
          notAvailableSkus = [];
          hasError = false;
          break;
        }

        deviceSkus = deviceSkus.length ? deviceSkus : [deviceSku];
        freebieSkus = freebieSkus.length ? freebieSkus : [];
        stockDetails = await getStockAvailability({
          deviceSkus,
          isMTPOSStockCacheEnabled: eshopMtposStockEnabled,
          groupId,
          accessorySkus,
          freebieSkus: freebiesSkuIds,
          checkStockFromStore
        });
        notAvailableSkus = stockDetails.unavailableItems;
        isAvailable = stockDetails.devices.status;
        hasError = stockDetails.error;
        if (deviceSku) {
          dispatch({
            type: ACTION_TYPES.FULFILMENT.GET_MTPOS_SUCCESS,
            payload: [
              ...stockDetails.devices.stock,
              ...stockDetails.freebies.stock
            ]
          });
        }
        break;
      }

      case STOCK_CHECK_TYPE.STORE_MULTIPLE_SKUS: {
        const skus = [
          deviceSku,
          ...accessories.map(accessory => accessory.skuId)
        ];
        const res = await dispatch(
          fulFillmentActions.getStoreAvailabilityForMultipleSkus({
            skus,
            storeIdPickup
          })
        );
        const storesAvailability = res?.reduce(
          (result, item, index) => {
            const firstItem = index === 0;
            if (firstItem || result.isAvailable) {
              const storesHaveStock = item?.itemAvailability?.filter(
                store =>
                  store.availableQty > 0 &&
                  (firstItem ||
                    result.availableStoreIds.includes(store.storeID))
              );

              result.isAvailable = storesHaveStock?.length > 0;
              result.availableStoreIds = storesHaveStock?.map(
                store => store.storeID
              );
            }
            return result;
          },
          {
            availableStoreIds: [],
            isAvailable: false
          }
        );

        isAvailable = storesAvailability?.isAvailable;
        availableStoreIds = storesAvailability?.availableStoreIds || [];
        break;
      }
      default:
        break;
    }

    setStockAvailable({
      status: isAvailable,
      notAvailableSkus,
      availableStoreIds,
      isFetched: true,
      stockDetails,
      error: hasError
    });
  };

  useInit(() => {
    if (apigeeTokenEnabled) {
      checkStocks();
    }
  }, [
    abTestingPending === false,
    deviceSku || deviceSkus?.length,
    apigeeTokenEnabled
  ]);

  useEffect(() => {
    let isLoading = false;
    switch (type) {
      case STOCK_CHECK_TYPE.STORE_MULTIPLE_SKUS:
      case STOCK_CHECK_TYPE.ALL:
        isLoading = mtposStock?.status === REQUEST_STATUS.PENDING;
        break;
      default:
        break;
    }
    setLoading(isLoading);
  }, [type, mtposStock?.status]);

  return { loading, stockAvailable };
};

export default useStockCheck;
